import type { PlayerId, RoundId, SessionEvent } from '@/schemas';
import { createSessionEventActivityResolver, getReplayableSessionEvents } from './event-activity';

/**
 * System events that cannot be undone
 */
const NON_UNDOABLE_EVENT_TYPES = [
  'SessionStarted',
  'SessionEnded',
  'SessionDeleted',
  'EventUndone',
  'EventRedone',
] as const;

/**
 * Check if an event type is undoable
 */
const isUndoableEventType = (eventType: SessionEvent['type']): boolean => {
  return !NON_UNDOABLE_EVENT_TYPES.includes(eventType as (typeof NON_UNDOABLE_EVENT_TYPES)[number]);
};

// Cache for state-before-event maps, keyed by events hash (sessionId + event count + latest event ID)
const stateBeforeEventCache = new Map<
  string,
  Map<SessionEvent['id'], { playersBefore: number; roundsBefore: number }>
>();

/**
 * Create cache key from events for state-before-event map
 */
const createEventsCacheKey = (replayableEvents: SessionEvent[]): string => {
  if (replayableEvents.length === 0) {
    return 'empty';
  }
  const firstEvent = replayableEvents[0];
  const lastEvent = replayableEvents.at(-1);
  if (!lastEvent) {
    return 'empty';
  }
  return `${firstEvent.sessionId}:${replayableEvents.length}:${lastEvent.id}`;
};

/**
 * Helper to update player counts when processing player events
 */
const updatePlayerCount = (event: SessionEvent, activePlayers: Map<PlayerId, boolean>): number => {
  if (event.type === 'PlayerJoined') {
    const currentlyActive = activePlayers.get(event.payload.playerId);
    if (!currentlyActive) {
      activePlayers.set(event.payload.playerId, true);
      return 1;
    }
  } else if (event.type === 'PlayerLeft' || event.type === 'PlayerRemoved') {
    const currentlyActive = activePlayers.get(event.payload.playerId);
    if (currentlyActive) {
      activePlayers.set(event.payload.playerId, false);
      return -1;
    }
  }
  return 0;
};

/**
 * Helper to update round counts when processing round events
 */
const updateRoundCount = (event: SessionEvent, activeRounds: Map<RoundId, boolean>): number => {
  if (event.type === 'RoundStarted') {
    const isActive = activeRounds.get(event.payload.roundId);
    if (!isActive) {
      activeRounds.set(event.payload.roundId, true);
      return 1;
    }
  } else if (event.type === 'RoundDeleted') {
    const isActive = activeRounds.get(event.payload.roundId);
    if (isActive) {
      activeRounds.set(event.payload.roundId, false);
      return -1;
    }
  }
  return 0;
};

/**
 * Helper to limit cache size to prevent memory leaks
 */
const limitCacheSize = (): void => {
  if (stateBeforeEventCache.size > 100) {
    const firstKey = stateBeforeEventCache.keys().next().value;
    if (firstKey) {
      stateBeforeEventCache.delete(firstKey);
    }
  }
};

/**
 * Track active player and round counts as events are processed.
 * Returns a map of event ID to state before that event.
 * Results are cached by events hash to avoid repeated computation.
 */
const buildStateBeforeEventMap = (
  replayableEvents: SessionEvent[],
): Map<SessionEvent['id'], { playersBefore: number; roundsBefore: number }> => {
  const cacheKey = createEventsCacheKey(replayableEvents);
  const cached = stateBeforeEventCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const sortedReplayable = [...replayableEvents].toSorted((a, b) => a.timestamp - b.timestamp);
  const stateBeforeEvent = new Map<
    SessionEvent['id'],
    { playersBefore: number; roundsBefore: number }
  >();
  const activePlayers = new Map<PlayerId, boolean>();
  const activeRounds = new Map<RoundId, boolean>();
  let activePlayerCount = 0;
  let activeRoundCount = 0;

  for (const event of sortedReplayable) {
    stateBeforeEvent.set(event.id, {
      playersBefore: activePlayerCount,
      roundsBefore: activeRoundCount,
    });

    activePlayerCount = Math.max(0, activePlayerCount + updatePlayerCount(event, activePlayers));
    activeRoundCount = Math.max(0, activeRoundCount + updateRoundCount(event, activeRounds));
  }

  // Cache the result and limit size
  stateBeforeEventCache.set(cacheKey, stateBeforeEvent);
  limitCacheSize();

  return stateBeforeEvent;
};

/**
 * Check if an event can be safely undone without leaving the session in an invalid state.
 */
const canSafelyUndoEvent = (
  event: SessionEvent,
  snapshot: { playersBefore: number; roundsBefore: number } | undefined,
): boolean => {
  if (!snapshot) {
    return true;
  }

  if (event.type === 'PlayerJoined' && snapshot.playersBefore === 0) {
    return false;
  }

  if (event.type === 'RoundStarted' && snapshot.roundsBefore === 0) {
    return false;
  }

  return true;
};

/**
 * Get the most recent undoable event from a list of events.
 * Returns undefined if no undoable events are available.
 * Prevents undoing events that would leave the session in an invalid state
 * (no rounds or no players).
 */
export const getUndoableEvent = (events: SessionEvent[]): SessionEvent | undefined => {
  const { resolver, events: replayableEvents } = getReplayableSessionEvents(events);
  const stateBeforeEvent = buildStateBeforeEventMap(replayableEvents);

  // Filter to active, undoable events, sorted by timestamp (most recent first)
  const undoableEvents = events
    .filter(
      (event) =>
        isUndoableEventType(event.type) &&
        resolver.isActive(event.id) &&
        event.type !== 'EventUndone' &&
        event.type !== 'EventRedone',
    )
    .toSorted((a, b) => b.timestamp - a.timestamp);

  for (const event of undoableEvents) {
    const snapshot = stateBeforeEvent.get(event.id);
    if (canSafelyUndoEvent(event, snapshot)) {
      return event;
    }
  }
};

/**
 * Get the most recent redoable event from a list of events.
 * A redoable event is one that has been undone (inactive) and can be redone.
 * Returns undefined if no redoable events are available.
 * Note: Redo is invalidated if any new undoable events occur after the undo operation.
 * This ensures that redo is only available when the undone event is still the most recent
 * undone event with no subsequent actions.
 */
export const getRedoableEvent = (events: SessionEvent[]): SessionEvent | undefined => {
  const resolver = createSessionEventActivityResolver(events);
  const references = resolver.getReferences();

  // Build map of undo/redo operations with timestamps from references
  const undoRedoOps = new Map<
    SessionEvent['id'],
    Array<{ eventId: SessionEvent['id']; kind: 'undo' | 'redo'; timestamp: number }>
  >();

  // Reuse the references map from resolver and enrich with timestamps
  for (const [eventId, refs] of references.entries()) {
    const ops: Array<{ eventId: SessionEvent['id']; kind: 'undo' | 'redo'; timestamp: number }> =
      [];
    for (const ref of refs) {
      const event = resolver.getEvent(ref.id);
      if (event) {
        ops.push({
          eventId: ref.id,
          kind: ref.kind,
          timestamp: event.timestamp,
        });
      }
    }
    if (ops.length > 0) {
      undoRedoOps.set(eventId, ops);
    }
  }

  // Find undone events (inactive, but undoable type)
  const undoneEvents = events.filter(
    (event) =>
      isUndoableEventType(event.type) &&
      !resolver.isActive(event.id) &&
      event.type !== 'EventUndone' &&
      event.type !== 'EventRedone',
  );

  // For each undone event, check if the most recent operation was an undo
  // (meaning it can be redone), and verify no new events occurred after the undo
  const redoableEvents = undoneEvents
    .map((event) => {
      const ops = undoRedoOps.get(event.id) ?? [];
      if (ops.length === 0) {
        return;
      }

      // Sort operations by timestamp (most recent first)
      const sortedOps = [...ops].toSorted((a, b) => b.timestamp - a.timestamp);

      // Check if the most recent operation is still active
      const mostRecentOp = sortedOps[0];
      if (!mostRecentOp || !resolver.isActive(mostRecentOp.eventId)) {
        return;
      }

      // If most recent operation was an undo, check if any new events occurred after it
      if (mostRecentOp.kind === 'undo') {
        const undoTimestamp = mostRecentOp.timestamp;

        // Check if there are any undoable events that occurred after this undo
        const hasNewEventsAfterUndo = events.some(
          (e) =>
            isUndoableEventType(e.type) &&
            e.type !== 'EventUndone' &&
            e.type !== 'EventRedone' &&
            e.timestamp > undoTimestamp &&
            resolver.isActive(e.id),
        );

        // If new events occurred after the undo, redo is invalidated
        if (hasNewEventsAfterUndo) {
          return;
        }

        return { event, lastUndoTimestamp: undoTimestamp };
      }
    })
    .filter(
      (item): item is { event: SessionEvent; lastUndoTimestamp: number } => item !== undefined,
    )
    .toSorted((a, b) => b.lastUndoTimestamp - a.lastUndoTimestamp);

  return redoableEvents[0]?.event;
};
